import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  Injector,
  OnInit,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { CardBodyComponent, CardComponent } from '@groundworks/ngx-groundworks-ui';
import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Observable, catchError, firstValueFrom, map, of, shareReplay } from 'rxjs';
import { ConfigService } from '../../config.service';
import { ProjectTypeConstants } from '../../constants/project-type.constants';
import { ProjectStatusConstants } from '../../constants/project-status.constants';
import {
  PendingCreditRequestInfo,
  OpportunityInfo,
  PaymentDetailsBilling,
  PaymentSummary,
  ProjectInfo,
} from '../../sdk/sdk';
import { AccountCreditRequestService } from '../../services/account-credit-request.service';
import { ChangeOrderEventsService } from '../../services/change-order-events.service';
import { ModalService } from '../../services/modal.service';
import { OpportunityService } from '../../services/opportunity.service';
import { PaymentRequestService } from '../../services/payment-request.service';
import { PaymentService } from '../../services/payment.service';
import { SendPaymentRequestError } from '../../services/send-payment-request-error';
import { formatUsd } from '../../utils/money';
import type { SendStatementResult } from '../send-statement-modal/send-statement-modal.component';
import { AttachmentsComponent } from '../attachments/attachments.component';
import { JobDetailsComponent } from '../job-details/job-details.component';
import { PaymentEditContext, PaymentsComponent } from '../payments/payments.component';
import { ProjectComponent } from '../project-details/project.component';
import { SelectedOpportunityComponent } from '../selected-opportunity/selected-opportunity.component';
import { TouchpointsComponent } from '../touchpoints/touchpoints.component';

const TOUCHPOINTS_FRAGMENT = 'touchpoints';

/** 1 for a descoped project, so it sorts after every active one (MOD-4990). */
const descopedRank = (project: ProjectInfo): number =>
  Number(project.projectStatusId === ProjectStatusConstants.Descoped);

/** 1 for the non-installable project, so it sorts after the installable ones. */
const nonInstallableRank = (project: ProjectInfo): number =>
  Number(project.projectTypeId === ProjectTypeConstants.NonInstallableServices);

@Component({
  selector: 'app-viewer',
  imports: [
    AttachmentsComponent,
    SelectedOpportunityComponent,
    JobDetailsComponent,
    PaymentsComponent,
    ProjectComponent,
    CardComponent,
    CardBodyComponent,
    TouchpointsComponent,
  ],
  templateUrl: './viewer.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ViewerComponent implements OnInit {
  private readonly opportunityService = inject(OpportunityService);
  private readonly configService = inject(ConfigService);
  private readonly modalService = inject(ModalService);
  private readonly paymentRequestService = inject(PaymentRequestService);
  private readonly paymentService = inject(PaymentService);
  private readonly acrService = inject(AccountCreditRequestService);
  private readonly changeOrderEvents = inject(ChangeOrderEventsService);
  private readonly route = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);
  private readonly injector = inject(Injector);

  private readonly touchpointsComponent = viewChild(TouchpointsComponent);
  private readonly touchpointsAnchor = viewChild<ElementRef<HTMLElement>>('touchpointsAnchor');
  private readonly attachmentsComponent = viewChild(AttachmentsComponent);

  private readonly fragment = toSignal(this.route.fragment);
  private scrolledForNavigation = false;

  readonly opportunityId = signal<string | null>(null);
  readonly opportunity = signal<OpportunityInfo | undefined>(undefined);
  readonly opportunityLoading = signal(false);
  readonly opportunityError = signal(false);
  readonly sendingStatement = signal(false);
  readonly paymentSummary = signal<PaymentSummary | undefined>(undefined);
  readonly paymentSummaryError = signal(false);
  readonly pendingCreditRequests = signal<PendingCreditRequestInfo[]>([]);
  readonly pendingCreditRequestsError = signal(false);
  readonly customerStatementUrl = signal<string | null>(null);
  private lastFetchedStatementUrlJobId: string | null = null;
  private statementUrlFetch$: Observable<string | null> | null = null;
  private viewClickInFlight = false;

  /**
   * Two ranks, applied in order.
   *
   * Descoped projects sort last of all (MOD-4990) — after the last active project, including after
   * the non-installable one. A descoped project is finished business kept on the screen for its
   * history, so it must never sit between two projects someone is still working.
   *
   * Non-installable projects carry tax and non-work line items and are never the project a user
   * opened the viewer to look at, so they sort after every installable one — last in the plain
   * row, rightmost in the horizontal strip. The server orders projects by id
   * (JobDataExtensions.ToJobInfo) and Array.prototype.sort is stable, so that order survives
   * inside each group. The copy keeps the sort off the opportunity signal's own payload.
   */
  readonly projects = computed(() => {
    const projects = this.opportunity()?.job?.projects ?? [];
    return [...projects].sort(
      (a, b) =>
        descopedRank(a) - descopedRank(b) ||
        nonInstallableRank(a) - nonInstallableRank(b));
  });
  readonly projectCount = computed(() => this.projects().length);
  readonly showProjectsScroll = computed(() => this.projectCount() > 3);

  readonly percentProjectPaid = computed<number | null>(() => {
    const summary = this.paymentSummary();
    if (!summary) return null;
    const paymentsReceived = summary.paymentsReceived ?? 0;
    const totalBalanceDue = summary.totalBalanceDue ?? 0;
    const total = paymentsReceived + totalBalanceDue;
    return total === 0 ? null : paymentsReceived / total;
  });

  readonly acr = computed<number | null>(() => {
    if (!this.configService.enablePayments || this.pendingCreditRequestsError()) return null;
    return this.pendingCreditRequests().reduce((sum, request) => sum + (request.creditAmount ?? 0), 0);
  });

  ngOnInit(): void {
    this.subscribeToRouteChanges();
  }

  /**
   * Subscribes to route query parameter changes to load the appropriate opportunity.
   * When opportunityId changes, triggers opportunity data loading.
   */
  private subscribeToRouteChanges(): void {
    this.route.queryParams
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((params) => {
        this.opportunityId.set(params['opportunityId'] ?? null);

        const idValue = this.opportunityId();
        if (idValue) {
          this.scrolledForNavigation = false;
          this.loadOpportunity(idValue);
          this.loadPaymentSummary(idValue);
          this.loadPendingCreditRequests(idValue);
        } else {
          this.paymentSummary.set(undefined);
          this.paymentSummaryError.set(false);
        }
      });
  }

  loadPaymentSummary(opportunityId: string): void {
    if (!this.configService.enablePayments) return;
    this.paymentSummaryError.set(false);
    this.paymentService.getPaymentSummary(opportunityId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (summary) => {
          this.paymentSummary.set(summary);
          this.paymentSummaryError.set(false);
        },
        error: (err) => {
          console.error('Error loading payment summary:', err);
          this.paymentSummary.set(undefined);
          this.paymentSummaryError.set(true);
        },
      });
  }

  private loadPendingCreditRequests(opportunityId: string | null | undefined): void {
    if (!this.configService.enablePayments || !opportunityId) return;
    this.pendingCreditRequestsError.set(false);
    this.acrService.getPendingCreditRequestsByOpportunityId(opportunityId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (requests) => {
          this.pendingCreditRequests.set(requests);
          this.pendingCreditRequestsError.set(false);
        },
        error: (err) => {
          console.error('Error loading pending credit requests:', err);
          this.pendingCreditRequests.set([]);
          this.pendingCreditRequestsError.set(true);
        },
      });
  }

  onJobCancelled(): void { this.reloadAfterJobChange('onJobCancelled'); }
  // The notification is deliberately NOT inside reloadAfterJobChange: onJobCancelled shares
  // that method, and job cancellation is out of scope for the change-order refresh story.
  onChangeOrderAdded(): void {
    this.reloadAfterJobChange('onChangeOrderAdded');
    this.changeOrderEvents.notifyChangeOrderAdded();
  }
  onPermitSaved(): void { this.touchpointsComponent()?.loadTouchpoints(); }

  onPostPayment(): void {
    const context = this.paymentModalContext();
    if (!context) return;
    this.refreshAfterClose(
      this.modalService.openPostPaymentModal(context.opportunityId, context.jobId, context.bcJobNumber)
    );
  }

  onEditPayment(context: PaymentEditContext): void {
    const modalContext = this.paymentModalContext();
    if (!modalContext) return;
    this.refreshAfterClose(
      this.modalService.openEditPaymentModal(modalContext.opportunityId, modalContext.jobId, context)
    );
  }

  onAccountCreditRequest(): void {
    const context = this.paymentModalContext();
    if (!context) return;
    this.refreshAfterClose(
      this.modalService.openAccountCreditRequestModal(context.opportunityId, context.jobId, context.bcJobNumber)
    );
  }

  onReviewAcr(): void {
    const context = this.paymentModalContext();
    if (!context) return;
    this.refreshAfterClose(
      this.modalService.openReviewAccountCreditRequestModal(
        context.opportunityId,
        context.jobId,
        context.bcJobNumber,
        this.pendingCreditRequests()
      )
    );
  }

  onViewPaymentHistory(): void {
    const id = this.opportunityId();
    if (!id) return;
    this.modalService.openPaymentHistoryModal(id);
  }

  private paymentModalContext(): { opportunityId: string; jobId: string | null; bcJobNumber: string | null } | null {
    const opportunityId = this.opportunityId();
    if (!opportunityId) return null;
    const job = this.opportunity()?.job;
    // The BC job number is the external identifier the opportunity read path resolves, not the
    // pre-BC legacyJobId — the payment modals must show the same number as the Job Details card
    // and Business Central. Null when the job has no external identifier yet.
    const bcJobNumber = job?.bcJobNumber == null ? null : String(job.bcJobNumber);
    return { opportunityId, jobId: job?.id ?? null, bcJobNumber };
  }

  private refreshAfterClose(modalRef: NgbModalRef): void {
    modalRef.closed
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.reloadAfterPaymentSubmitted());
  }

  // Fetched per modal open rather than cached with the payment summary, so the ceiling the modal
  // shows is as fresh as possible at send time. A failure resolves to null instead of throwing:
  // the modal still opens (recipient picker and Add New Contact stay usable) and renders its own
  // error, which is where the user is told about it.
  private async loadStatementBilling(): Promise<PaymentDetailsBilling | null> {
    const opportunityId = this.opportunityId();
    if (!opportunityId) return null;
    return firstValueFrom(
      this.paymentService.getPaymentDetails(opportunityId).pipe(
        map((details) => details.billing),
        catchError((err) => {
          console.error('Loading payment details for the statement modal failed', err);
          return of(null);
        }),
      ),
    );
  }

  async onSendCustomerStatement(): Promise<void> {
    if (this.sendingStatement()) return;
    const job = this.opportunity()?.job;
    if (!job) {
      console.warn('Send Customer Statement clicked with no job loaded', { opportunity: this.opportunity() });
      return;
    }
    this.sendingStatement.set(true);
    try {
      const contacts = this.opportunity()?.contacts ?? [];
      const billing = await this.loadStatementBilling();
      const result = await this.modalService
        .openSendStatementModal(contacts, this.opportunityId() ?? null, billing)
        .result.catch((reason) => {
          if (reason instanceof Error) throw reason;
          return null;
        });
      if (!result) return;

      const { contactId, contactName, amount } = result as SendStatementResult;
      await firstValueFrom(this.paymentRequestService.sendForJob(job.id, contactId, amount));
      const subject = amount == null ? 'A statement' : `A statement for ${formatUsd(amount)}`;
      const message = contactName
        ? `${subject} was successfully sent to ${contactName}.`
        : `${subject} was successfully sent to the customer.`;
      this.modalService.openInfoModal('Send Customer Statement', message, 'bi bi-check-circle-fill');
    } catch (err) {
      console.error('Send Customer Statement failed', err);
      const errorMessage = messageForSendError(err);
      this.modalService.openErrorModal('Send Customer Statement', errorMessage);
    } finally {
      this.sendingStatement.set(false);
    }
  }

  private reloadAfterPaymentSubmitted(): void {
    const id = this.opportunityId();
    if (id) {
      this.loadOpportunity(id);
      this.loadPaymentSummary(id);
      this.loadPendingCreditRequests(id);
    }
    this.touchpointsComponent()?.loadTouchpoints();
    this.attachmentsComponent()?.loadAttachments();
  }

  private reloadAfterJobChange(caller: string): void {
    const id = this.opportunityId();
    if (id) {
      this.loadOpportunity(id);
    } else {
      console.warn(`${caller}: opportunityId is not set; cannot reload opportunity data.`);
    }
    this.touchpointsComponent()?.loadTouchpoints();
  }

  /**
   * Loads opportunity data from the API.
   * @param opportunityId - The ID of the opportunity to load
   */
  private loadOpportunity(opportunityId: string): void {
    this.opportunityLoading.set(true);
    this.opportunityError.set(false);

    this.opportunityService.getOpportunity(opportunityId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
      next: (opportunity) => {
        this.opportunity.set(opportunity);
        this.opportunityLoading.set(false);
        this.opportunityError.set(false);
        this.scrollToTouchpointsIfRequested();
        const jobId = opportunity?.job?.id;
        if (jobId) this.loadCustomerStatementUrl(jobId);
      },
      error: (error) => {
        console.error('Error loading opportunity:', error);
        this.opportunityLoading.set(false);
        this.opportunityError.set(true);
      },
    });
  }

  private loadCustomerStatementUrl(jobId: string): void {
    if (!this.configService.enablePayments) return;
    if (this.lastFetchedStatementUrlJobId === jobId) return;
    this.startStatementUrlFetch(jobId);
    this.statementUrlFetch$!
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (url) => this.customerStatementUrl.set(url),
        error: (err) => {
          // Clear the cached failed observable so the click handler issues a
          // fresh request instead of replaying the error indefinitely.
          console.error('Customer statement pre-fetch failed (will retry on click)', err);
          this.statementUrlFetch$ = null;
          this.lastFetchedStatementUrlJobId = null;
        },
      });
  }

  onViewCustomerStatement(): void {
    const job = this.opportunity()?.job;
    if (!job) return;

    const cached = this.customerStatementUrl();
    if (cached) {
      this.openInNewTab(cached);
      return;
    }

    // Guard against rapid double-clicks while the first fetch is in flight.
    if (this.viewClickInFlight) return;
    this.viewClickInFlight = true;

    // Reuse the in-flight pre-fetch if one exists; otherwise start one so a
    // subsequent click also reuses the same observable (no double-open).
    if (!this.statementUrlFetch$) this.startStatementUrlFetch(job.id);
    this.statementUrlFetch$!
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (url) => {
          this.viewClickInFlight = false;
          if (!url) {
            this.modalService.openInfoModal(
              'View Customer Statement',
              'A customer statement is not available for this job yet.',
              'bi bi-info-circle-fill',
            );
            return;
          }
          this.customerStatementUrl.set(url);
          this.openInNewTab(url);
        },
        error: (err) => {
          this.viewClickInFlight = false;
          console.error('View Customer Statement fetch failed', err);
          // Clear the cached failed observable so the next click retries.
          this.statementUrlFetch$ = null;
          this.lastFetchedStatementUrlJobId = null;
          this.modalService.openErrorModal(
            'View Customer Statement',
            'Could not load the customer statement. Please try again.',
          );
        },
      });
  }

  private startStatementUrlFetch(jobId: string): void {
    this.lastFetchedStatementUrlJobId = jobId;
    this.statementUrlFetch$ = this.paymentRequestService.getStatementUrlForJob(jobId)
      .pipe(shareReplay({ bufferSize: 1, refCount: false }));
  }

  private openInNewTab(url: string): void {
    const newWin = window.open(url, '_blank');
    if (!newWin) {
      this.modalService.openErrorModal(
        'View Customer Statement',
        'Your browser blocked the new tab. Please allow popups for this site and try again.',
      );
      return;
    }
    newWin.opener = null;
  }

  private scrollToTouchpointsIfRequested(): void {
    if (this.scrolledForNavigation || this.fragment() !== TOUCHPOINTS_FRAGMENT) return;
    this.scrolledForNavigation = true;
    afterNextRender(
      () => this.touchpointsAnchor()?.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'start' }),
      { injector: this.injector },
    );
  }
}

// The server owns the wording for a failed send: ProblemDetails.title is the display-safe label for
// every coded payment failure, so it is rendered verbatim rather than mirrored into a client-side
// map that can drift from the server catalog. `detail` is deliberately NOT shown -- it stays
// diagnostic and carries job GUIDs, Patch customer ids and raw phone digits.

const GENERIC_SEND_FAILURE = 'Could not send payment request. Please try again.';

// Titles ExceptionHandlingMiddleware emits for a fault no payment code claimed. They name the HTTP
// condition rather than what went wrong with the send, so they get client wording instead.
const FRAMEWORK_TITLES = new Set(['Internal Server Error', 'Unexpected Error', 'Bad Gateway']);

// The server's title for this code names the payment vendor, which is an internal detail. This is
// the server catalog's own sentence for the code with the vendor name dropped.
const MERCHANT_NOT_CONFIGURED_MESSAGE =
  "This branch isn't set up to take payments yet. "
  + 'Ask an administrator to complete the branch payment setup.';

type SendError = Partial<SendPaymentRequestError> | null | undefined;

function messageForSendError(err: unknown): string {
  const typed = err as SendError;
  return clientWordingFor(typed) ?? serverTitleFor(typed) ?? GENERIC_SEND_FAILURE;
}

// Failures the server cannot word for us. Checked before the title so a usable-looking title never
// wins over wording that carries something the title cannot.
function clientWordingFor(err: SendError): string | null {
  if (err?.status === 0) return 'Could not reach the server. Check your connection, then resend.';
  if (err?.status === 401 || err?.status === 403) {
    return 'You are not authorized to send payment requests. Sign in again, then resend.';
  }
  return codeWordingFor(err);
}

// The three codes whose title cannot say what the user needs: MERCHANT_NOT_CONFIGURED's names the
// payment vendor, AMOUNT_EXCEEDS_AMOUNT_DUE's omits the figure actually due, and
// AMOUNT_MUST_BE_POSITIVE's is a generic "Invalid request" while its detail is display-safe.
function codeWordingFor(err: SendError): string | null {
  switch (err?.errorCode) {
    case 'MERCHANT_NOT_CONFIGURED':
      return MERCHANT_NOT_CONFIGURED_MESSAGE;
    // Both amount codes are backstops: the modal caps the amount at the ceiling it fetched, so they
    // are only reachable when the ceiling moved between that fetch and the send.
    case 'AMOUNT_EXCEEDS_AMOUNT_DUE':
      return amountExceedsMessage(err?.extensions?.['amountDueNow']);
    case 'AMOUNT_MUST_BE_POSITIVE':
      return err?.detail ?? 'Enter an amount greater than $0.00, then resend.';
    default:
      return null;
  }
}

function amountExceedsMessage(dueNow: unknown): string {
  if (dueNow === 0) return 'This job has no balance currently due, so no amount can be requested.';
  return typeof dueNow === 'number'
    ? `That amount is more than the ${formatUsd(dueNow)} currently due on this job. Enter a lower amount, then resend.`
    : 'That amount is more than the amount currently due on this job. Enter a lower amount, then resend.';
}

// Returns null when no usable title reached us, leaving the caller to fall through to client wording.
function serverTitleFor(err: SendError): string | null {
  const title = err?.title;
  if (typeof title !== 'string' || title.length === 0 || FRAMEWORK_TITLES.has(title)) return null;

  // A decline's title says only that the send did not complete; the processor's reason says why.
  const declineReason = err?.extensions?.['declineReason'];
  return typeof declineReason === 'string' && declineReason.length > 0
    ? `${title}: ${declineReason}.`
    : title;
}
